
// Logger service for application-wide logging without Sentry

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type LogContext = Record<string, any>;

interface LogOptions {
  tags?: Record<string, string>;
  persistent?: boolean;
  metadata?: Record<string, any>;
}

class Logger {
  private env: string;

  constructor() {
    this.env = import.meta.env.MODE || 'development';
  }

  debug(message: string | Error, context?: LogContext): void {
    this.log('debug', message, context);
  }

  info(message: string | Error, context?: LogContext): void {
    this.log('info', message, context);
  }

  warn(message: string | Error, context?: LogContext): void {
    this.log('warn', message, context);
  }

  error(message: string | Error, context?: LogContext): void {
    this.log('error', message, context);
  }

  trackEvent(eventName: string, data?: any, options?: LogOptions): void {
    const eventData = {
      event: eventName,
      data: data || {},
      timestamp: new Date().toISOString(),
      ...options
    };

    console.log(`[EVENT] ${eventName}:`, eventData);
    
    // In production, send to analytics services
    if (window.plausible && this.env === 'production') {
      window.plausible(eventName, { 
        props: data,
        callback: options?.tags?.callback ? () => options.tags?.callback : undefined
      });
    }
  }

  private log(level: LogLevel, message: string | Error, context?: LogContext): void {
    const timestamp = new Date().toISOString();
    const messageStr = message instanceof Error ? message.message : message;
    const stack = message instanceof Error ? message.stack : undefined;
    
    const logData = {
      level,
      message: messageStr,
      timestamp,
      context: context || {},
      stack,
      env: this.env
    };
    
    // Simple console logging
    console[level](`[${timestamp}] [${level.toUpperCase()}]:`, messageStr, context || '');
    
    // For production, you might want to add a service to collect logs
    if (this.env === 'production' && (level === 'error' || level === 'warn')) {
      // Add your logging service implementation here
      this.persistLog(logData);
    }
  }

  // Method to persist logs to backend if needed
  private persistLog(logData: any): void {
    // This is a placeholder for implementing persistence
    // For example, sending logs to a backend service
    try {
      // Example implementation using fetch
      // fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logData)
      // });

      // For now, just add to console in a different format
      console.debug('[PERSISTED LOG]', logData);
    } catch (e) {
      console.error('Failed to persist log:', e);
    }
  }
}

export const logger = new Logger();
